import { getBrandingSync, loadBranding } from './branding.js'
import { clientLoginButtonHtml } from './loginModal.js'
import { getTheme, toggleTheme } from './theme.js'
import { isClientLoggedIn } from './clientAuth.js'

const APP_NAME = import.meta.env.VITE_APP_NAME || 'Centro de soporte'

export function escapeHtml(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/**
 * Alinea <link rel="icon"> / apple-touch-icon al mismo URL que el <img> de cabecera.
 * Preferencia: branding.logo_url; fallback estático /favicon.png (generado 1:1 en deploy).
 */
export function applyBrandIcons(branding) {
  const b = branding || getBrandingSync()
  const href = b.favicon_url || b.logo_url || '/favicon.png'
  if (!href) return

  const ensureLink = (rel, attrs = {}) => {
    let el = document.head.querySelector(`link[rel="${rel}"]`)
    if (!el) {
      el = document.createElement('link')
      el.setAttribute('rel', rel)
      document.head.appendChild(el)
    }
    el.setAttribute('href', href)
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v))
    return el
  }

  ensureLink('icon', { type: 'image/png' })
  ensureLink('apple-touch-icon')

  // Quitar posibles SVG genéricos (casco) que compitan con el logo
  document.head.querySelectorAll('link[rel="icon"][href*="favicon.svg"]').forEach((el) => el.remove())
}

export function contactFooterHtml(branding) {
  const b = branding || getBrandingSync()
  const email = b.support_email || b.managing_company?.email || 'soporte@aghsistemas.com'
  const company =
    b.managing_company?.commercial_name || b.organization_name || 'AGH Sistemas y Telecomunicaciones'
  const web = b.managing_company?.website || 'https://aghsistemas.com'

  return `
    <div class="portal-footer-contact">
      <strong>${escapeHtml(company)}</strong>
      <span class="portal-footer-sep">·</span>
      <a href="mailto:${escapeHtml(email)}">${escapeHtml(email)}</a>
      <span class="portal-footer-sep">·</span>
      <a href="${escapeHtml(web)}" target="_blank" rel="noopener">${escapeHtml(web.replace(/^https?:\/\//, ''))}</a>
      <span class="portal-footer-sep">·</span>
      <a href="/legal/terminos/">Condiciones</a>
      <span class="portal-footer-sep">·</span>
      <a href="/legal/privacidad/">Privacidad</a>
      <span class="portal-footer-sep">·</span>
      <a href="/legal/eliminacion-datos/">Eliminación de datos</a>
    </div>`
}

function layoutClass(layout) {
  if (layout === 'login-only') return ' portal-main--login-only'
  if (layout === 'home') return ' portal-main--home'
  if (layout === 'dash') return ' portal-main--dash'
  return ''
}

export function shell({ title, bodyHtml, branding, layout = 'default' }) {
  const b = branding || getBrandingSync()
  const name = b.portal_title || APP_NAME
  document.title = `${title} — ${name}`

  // Favicon / apple-touch = mismo asset que el logo de cabecera (1:1)
  applyBrandIcons(b)

  if (b.primary_color) {
    document.documentElement.style.setProperty('--portal-accent', b.primary_color)
  }

  const theme = getTheme()
  const themeLabel = theme === 'light' ? 'Oscuro' : 'Claro'
  const themeIco = theme === 'light' ? '☾' : '☀'
  const clientCta = isClientLoggedIn()
    ? `<a href="/mi-instalacion" data-link class="support-header-btn support-header-btn--primary"><span class="support-header-btn-ico" aria-hidden="true">📊</span> Mi portal</a>
        <a href="/mi-instalacion/nueva-incidencia" data-link class="support-header-btn support-header-btn--ghost-accent"><span class="support-header-btn-ico" aria-hidden="true">✎</span> Crear ticket</a>
        <a href="/mi-instalacion/presupuesto-it" data-link class="support-header-btn support-header-btn--ghost-accent"><span class="support-header-btn-ico" aria-hidden="true">€</span> Presupuesto</a>`
    : clientLoginButtonHtml()

  return `
  <div class="portal-shell">
    <header class="portal-header">
      <a class="portal-brand" href="/" data-link>
        ${b.logo_url ? `<img class="portal-logo" src="${escapeHtml(b.logo_url)}" alt="" width="40" height="40" />` : ''}
        <div class="portal-brand-text">
          ${escapeHtml(name)}
          <small>${escapeHtml(b.organization_name || 'AGH Sistemas')}</small>
        </div>
      </a>
      <nav class="portal-nav" aria-label="Principal">
        <a href="/" data-link>Inicio</a>
        ${isClientLoggedIn() ? '' : `<a href="/nuevo" data-link>Nueva incidencia</a>`}
        <a href="/ia" data-link>Asistente IA</a>
        ${clientCta}
        <button type="button" class="support-header-btn support-header-btn--ghost" data-theme-toggle title="Cambiar tema">
          <span class="support-header-btn-ico" aria-hidden="true">${themeIco}</span> ${themeLabel}
        </button>
        <button type="button" class="support-header-btn support-header-btn--ghost-accent" data-pwa-install hidden>Instalar app</button>
      </nav>
    </header>
    <main class="portal-main${layoutClass(layout)}">${bodyHtml}</main>
    <footer class="portal-footer">
      ${contactFooterHtml(b)}
    </footer>
  </div>`
}

/** Prefetch branding (no bloquea UI). */
export function ensureBranding() {
  return loadBranding()
}

export function wireShellChrome(root, { navigate } = {}) {
  root.querySelectorAll('[data-theme-toggle]').forEach((btn) => {
    btn.addEventListener('click', () => {
      toggleTheme()
      // Re-render soft: actualizar icono/texto del botón
      const t = getTheme()
      btn.innerHTML = `<span aria-hidden="true">${t === 'light' ? '☾' : '☀'}</span> ${t === 'light' ? 'Oscuro' : 'Claro'}`
    })
  })
}

/**
 * Valida campos requeridos y marca errores visibles.
 * @param {HTMLFormElement} form
 * @param {Array<{ name: string, label: string, test?: (value: string, form: HTMLFormElement) => boolean }>} fields
 * @returns {{ ok: boolean, missing: string[] }}
 */
export function validateRequiredFields(form, fields) {
  clearFieldErrors(form)
  const missing = []
  for (const field of fields) {
    const el = form.elements.namedItem(field.name)
    if (!el || typeof el === 'RadioNodeList') continue
    const value = String(el.value || '').trim()
    const ok = field.test ? field.test(value, form) : value.length > 0
    if (!ok) {
      missing.push(field.label)
      markFieldError(el, field.label)
    }
  }
  return { ok: missing.length === 0, missing }
}

export function clearFieldErrors(root = document) {
  const scopes = []
  if (root instanceof Element) {
    scopes.push(root)
    if (root.classList.contains('portal-field')) {
      root.classList.remove('is-invalid')
      root.querySelector('.portal-field-error')?.remove()
      root.querySelectorAll('.portal-input, .portal-textarea, .portal-select')
        .forEach((el) => el.classList.remove('is-invalid'))
      return
    }
  }
  root.querySelectorAll('.portal-field.is-invalid').forEach((wrap) => {
    wrap.classList.remove('is-invalid')
    wrap.querySelector('.portal-field-error')?.remove()
  })
  root.querySelectorAll('.portal-input.is-invalid, .portal-textarea.is-invalid, .portal-select.is-invalid')
    .forEach((el) => el.classList.remove('is-invalid'))
}

function markFieldError(el, label) {
  el.classList.add('is-invalid')
  const wrap = el.closest('.portal-field') || el.parentElement
  if (!wrap) return
  wrap.classList.add('is-invalid')
  if (!wrap.querySelector('.portal-field-error')) {
    const err = document.createElement('p')
    err.className = 'portal-field-error'
    err.textContent = `Falta: ${label}`
    wrap.appendChild(err)
  }
  if (typeof el.focus === 'function' && !document.querySelector('.portal-input.is-invalid:focus, .portal-textarea.is-invalid:focus, .portal-select.is-invalid:focus')) {
    try { el.focus({ preventScroll: false }) } catch (_) { el.focus() }
  }
}

export function missingFieldsMessage(missing) {
  if (!missing.length) return ''
  if (missing.length === 1) return `Completa el campo «${missing[0]}».`
  return `Completa estos campos: ${missing.join(', ')}.`
}
